import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  FOLDER_CATEGORY_VALUES,
  type FolderCategory,
} from '@content-engine/shared';
import { ApiError } from '../../lib/api.ts';
import {
  completeFilmUpload,
  fetchFilms,
  filmFromR2Key,
  filmFromUrl,
  initFilmUpload,
  scanFilms,
  scrapeFilms,
  uploadFilmFileToR2,
} from '../../lib/queries.ts';
import type { FilmListItem, ScanResult } from '../../lib/types.ts';
import { AuthorFilter } from '../../components/AuthorFilter.tsx';
import { ArchivedChip, ShowArchivedCheckbox } from '../../components/archive/index.ts';
import { FilmArchiveDialog } from './FilmArchiveDialog.tsx';

function formatDuration(sec: number | null): string {
  if (sec === null || sec <= 0) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  }
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

export function FilmsListPage(): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showScan, setShowScan] = useState(false);
  const [showIngest, setShowIngest] = useState(false);
  const [showScrape, setShowScrape] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<FilmListItem | null>(null);

  const [author, setAuthor] = useState<'me' | 'all' | string>('me');

  const filmsQuery = useQuery({
    queryKey: ['films', 'with-counts', author, showArchived],
    queryFn: () => fetchFilms({ include: 'counts', createdBy: author, showArchived }),
    refetchInterval: (q) => {
      const list = q.state.data?.films ?? [];
      const inFlight = list.some((f) => f.status === 'pending_inventory' || f.status === 'inventorying');
      return inFlight ? 3_000 : false;
    },
  });

  const films = filmsQuery.data?.films ?? [];
  const filmCounts = filmsQuery.data?.counts ?? null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('films.title')}</h1>
          <div className="sub">{t('films.subtitle')}</div>
        </div>
        <div className="filters" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <AuthorFilter value={author} onChange={setAuthor} />
          <ShowArchivedCheckbox checked={showArchived} onChange={setShowArchived} />
          <button onClick={() => setShowScan(true)}>
            {t('films.scanButton')}
          </button>
          <button onClick={() => setShowScrape(true)}>
            {t('films.scrapeButton')}
          </button>
          <button className="primary" onClick={() => setShowIngest(true)}>
            {t('films.ingestButton')}
          </button>
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <h2>
            {t('films.title')} <span className="count">{films.length}</span>
          </h2>
        </div>
        <div className="section-body" style={{ padding: 0 }}>
          {filmsQuery.isLoading && <div style={{ padding: 16 }}>{t('common.loading')}</div>}
          {filmsQuery.isError && (
            <div style={{ padding: 16, color: 'var(--hot)' }}>
              {t('films.loadError', { message: (filmsQuery.error as Error).message })}
            </div>
          )}
          {films.length === 0 && !filmsQuery.isLoading && (
            <div style={{ padding: 24, color: 'var(--text-3)' }}>{t('films.noFilms')}</div>
          )}
          {films.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>{t('films.tableTitle')}</th>
                  <th>{t('films.tableAdded')}</th>
                  <th>{t('films.tableYear')}</th>
                  <th>{t('films.tableCategory')}</th>
                  <th>{t('films.tableLanguage')}</th>
                  <th className="num">{t('films.tableDuration')}</th>
                  <th>{t('films.tableSrt')}</th>
                  <th>{t('films.tableTmdb')}</th>
                  <th>{t('films.tableStatus')}</th>
                  <th>{t('films.tableContent')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {films.map((f) => (
                  <FilmRow
                    key={f.id}
                    film={f}
                    counts={filmCounts?.[f.id] ?? null}
                    onArchive={() => setArchiveTarget(f)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showScan && (
        <ScanDialog
          onClose={() => setShowScan(false)}
          onSuccess={() => {
            setShowScan(false);
            void queryClient.invalidateQueries({ queryKey: ['films'] });
          }}
        />
      )}
      {showIngest && (
        <IngestDialog
          onClose={() => setShowIngest(false)}
          onSuccess={() => {
            setShowIngest(false);
            void queryClient.invalidateQueries({ queryKey: ['films'] });
          }}
        />
      )}
      {showScrape && (
        <ScrapeDialog
          onClose={() => setShowScrape(false)}
          onSuccess={() => {
            setShowScrape(false);
            void queryClient.invalidateQueries({ queryKey: ['films'] });
          }}
        />
      )}
      {archiveTarget && (
        <FilmArchiveDialog
          filmId={archiveTarget.id}
          file={archiveTarget.file}
          onClose={() => setArchiveTarget(null)}
        />
      )}
    </>
  );
}

/** "720p"/"1080p" chip next to the film name: red below 1080 (weak source —
 *  every clip cut from it inherits that), green otherwise. min(w,h) so
 *  vertical 1080×1920 counts as 1080p. Hidden until inventory ran. */
function QualityBadge({ width, height }: { width: number | null; height: number | null }): React.ReactElement | null {
  if (width == null || height == null) return null;
  const p = Math.min(width, height);
  const good = p >= 1080;
  return (
    <span
      title={`${width}×${height}`}
      style={{
        marginLeft: 6,
        fontSize: 10,
        padding: '1px 6px',
        borderRadius: 8,
        verticalAlign: 'middle',
        background: good ? 'rgba(22,163,74,0.15)' : 'rgba(220,38,38,0.15)',
        color: good ? '#16a34a' : '#dc2626',
      }}
    >
      {p}p
    </span>
  );
}

/** "youtube.com" for a source link — enough to tell YouTube from Vimeo at a glance. */
function sourceHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** Local date + time of ingestion, e.g. "29.08.2026, 13:06". */
function formatAdded(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function FilmRow({
  film,
  counts,
  onArchive,
}: {
  film: FilmListItem;
  counts: { moments: number; clips: number } | null;
  onArchive: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <tr className={film.archivedAt ? 'arch' : undefined}>
      <td>
        <Link to={`/films/${film.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
          <div className="name" style={{ color: 'var(--neon)' }}>
            {film.parsedTitle ?? film.rawName}
            <QualityBadge width={film.width} height={film.height} />
          </div>
        </Link>
        <div className="desc" style={{ fontSize: 11, color: 'var(--text-3)' }}>
          {film.sourceUrl ? (
            <a href={film.sourceUrl} target="_blank" rel="noreferrer" title={film.sourceUrl} style={{ color: 'inherit' }}>
              {sourceHost(film.sourceUrl)} · {film.rawName}
            </a>
          ) : (
            film.rawName
          )}
        </div>
        {film.archivedAt && (
          <div style={{ marginTop: 3 }}>
            <ArchivedChip archivedAt={film.archivedAt} />
          </div>
        )}
      </td>
      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }} title={film.createdAt}>
        {formatAdded(film.createdAt)}
      </td>
      <td>{film.parsedYear ?? '—'}</td>
      <td>{film.folderCategory}</td>
      <td>{film.language}</td>
      <td className="num">{formatDuration(film.durationSec)}</td>
      <td>{film.hasSrt ? t('films.hasSrt') : t('films.noSrt')}</td>
      <td>
        {film.tmdbPosterUrl ? (
          <img
            src={film.tmdbPosterUrl}
            alt=""
            style={{ width: 30, height: 45, objectFit: 'cover', borderRadius: 3 }}
          />
        ) : (
          '—'
        )}
      </td>
      <td>
        <span className={`pill ${film.status === 'inventoried' ? 'live' : ''}`}>
          {t(`films.status_${film.status}`)}
        </span>
        {film.errorMessage && (
          <div style={{ fontSize: 10, color: 'var(--hot)', marginTop: 4, maxWidth: 220 }}>
            {film.errorMessage.slice(0, 120)}
          </div>
        )}
      </td>
      <td style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
        {counts ? (
          <>
            <span style={{ color: counts.moments > 0 ? 'var(--text-2)' : undefined }}>
              {t('films.momentsCount', { count: counts.moments })}
            </span>
            {' · '}
            <span style={{ color: counts.clips > 0 ? 'var(--neon)' : undefined }}>
              {t('films.clipsCount', { count: counts.clips })}
            </span>
          </>
        ) : (
          '—'
        )}
      </td>
      <td>
        {/* Удаления в пользовательском UI больше нет: архивация вместо него.
            Диалог сам объясняет последствия — confirm() не нужен. */}
        {!film.archivedAt && (
          <button type="button" onClick={onArchive} style={{ fontSize: 11, padding: '4px 10px' }}>
            {t('films.archiveButton')}
          </button>
        )}
      </td>
    </tr>
  );
}

function IngestDialog(props: { onClose: () => void; onSuccess: () => void }): React.ReactElement {
  const { t } = useTranslation();
  const [source, setSource] = useState<'file' | 'url' | 'r2key'>('file');
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [r2Key, setR2Key] = useState('');
  const [r2Name, setR2Name] = useState('');
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      setError(null);
      if (source === 'file') {
        if (!file) throw new Error('No file selected');
        setUploadProgress(t('films.ingestModal.uploading'));
        const { filmId, putUrl } = await initFilmUpload({ filename: file.name, contentType: file.type });
        await uploadFilmFileToR2(putUrl, file);
        await completeFilmUpload({ filmId, sizeBytes: file.size });
      } else if (source === 'url') {
        if (!url.trim()) throw new Error('No URL');
        await filmFromUrl({ url: url.trim() });
      } else {
        if (!r2Key.trim()) throw new Error('No R2 key');
        await filmFromR2Key({ key: r2Key.trim(), ...(r2Name.trim() ? { name: r2Name.trim() } : {}) });
      }
    },
    onSuccess: () => props.onSuccess(),
    onError: (err) => {
      setUploadProgress(null);
      setError((err as Error).message);
    },
    onSettled: () => setUploadProgress(null),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    mutation.mutate();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
      onClick={props.onClose}
    >
      <div
        style={{
          width: 520,
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          padding: 28,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, marginBottom: 16 }}>{t('films.ingestModal.title')}</h2>
        <form onSubmit={submit}>
          {/* Source radio */}
          <div className="form-row" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              <input
                type="radio"
                name="ingest-source"
                checked={source === 'file'}
                onChange={() => setSource('file')}
              />
              {t('films.ingestModal.sourceFile')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              <input
                type="radio"
                name="ingest-source"
                checked={source === 'url'}
                onChange={() => setSource('url')}
              />
              {t('films.ingestModal.sourceUrl')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              <input
                type="radio"
                name="ingest-source"
                checked={source === 'r2key'}
                onChange={() => setSource('r2key')}
              />
              {t('films.ingestModal.sourceR2Key')}
            </label>
          </div>

          {source === 'file' && (
            <div className="form-row">
              <label>{t('films.ingestModal.fileLabel')}</label>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                required
              />
            </div>
          )}
          {source === 'url' && (
            <div className="form-row">
              <label>{t('films.ingestModal.urlLabel')}</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t('films.ingestModal.urlPlaceholder')}
                required
              />
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                {t('films.ingestModal.urlHint')}
              </div>
            </div>
          )}
          {source === 'r2key' && (
            <>
              <div className="form-row">
                <label>{t('films.ingestModal.r2KeyLabel')}</label>
                <input
                  type="text"
                  value={r2Key}
                  onChange={(e) => setR2Key(e.target.value)}
                  placeholder={t('films.ingestModal.r2KeyPlaceholder')}
                  required
                  style={{ fontFamily: 'var(--mono)', fontSize: 12 }}
                />
              </div>
              <div className="form-row">
                <label>{t('films.ingestModal.r2NameLabel')}</label>
                <input
                  type="text"
                  value={r2Name}
                  onChange={(e) => setR2Name(e.target.value)}
                  placeholder={t('films.ingestModal.r2NamePlaceholder')}
                />
              </div>
            </>
          )}

          {uploadProgress && (
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>
              {uploadProgress}
            </div>
          )}

          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: 'rgba(255,61,110,0.1)',
                border: '1px solid rgba(255,61,110,0.3)',
                borderRadius: 6,
                color: 'var(--hot)',
                fontSize: 12,
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={props.onClose}>
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="primary"
              disabled={
                mutation.isPending ||
                (source === 'file' && !file) ||
                (source === 'url' && !url.trim()) ||
                (source === 'r2key' && !r2Key.trim())
              }
            >
              {mutation.isPending
                ? t('films.ingestModal.submitting')
                : t('films.ingestModal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ScanDialog(props: { onClose: () => void; onSuccess: () => void }): React.ReactElement {
  const { t } = useTranslation();
  const [path, setPath] = useState('');
  const [category, setCategory] = useState<FolderCategory>('films');
  const [dryRun, setDryRun] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);

  const scanMutation = useMutation({
    mutationFn: () => scanFilms({ path, folderCategory: category, dryRun }),
    onSuccess: (r) => {
      setResult(r);
      if (!dryRun) props.onSuccess();
    },
  });

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    scanMutation.mutate();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
      onClick={props.onClose}
    >
      <div
        style={{
          width: 520,
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          padding: 28,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, marginBottom: 4 }}>{t('films.scanModal.title')}</h2>
        <div style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 20 }}>
          {t('films.scanModal.subtitle')}
        </div>
        <form onSubmit={submit}>
          <div className="form-row">
            <label>{t('films.scanModal.pathLabel')}</label>
            <input
              type="text"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder={t('films.scanModal.pathPlaceholder')}
              required
              autoFocus
              style={{ fontFamily: 'var(--mono)', fontSize: 12 }}
            />
          </div>
          <div className="form-row">
            <label>{t('films.scanModal.categoryLabel')}</label>
            <select value={category} onChange={(e) => setCategory(e.target.value as FolderCategory)}>
              {FOLDER_CATEGORY_VALUES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              id="dryRun"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            <label htmlFor="dryRun" style={{ margin: 0 }}>
              {t('films.scanModal.dryRunLabel')}
            </label>
          </div>

          {scanMutation.error instanceof ApiError && (
            <div
              style={{
                padding: '10px 12px',
                background: 'rgba(255,61,110,0.1)',
                border: '1px solid rgba(255,61,110,0.3)',
                borderRadius: 6,
                color: 'var(--hot)',
                fontSize: 12,
                marginBottom: 12,
              }}
            >
              {scanMutation.error.message}
            </div>
          )}

          {result && (
            <div
              style={{
                padding: '10px 12px',
                background: 'rgba(0,255,128,0.1)',
                border: '1px solid rgba(0,255,128,0.3)',
                borderRadius: 6,
                color: 'var(--neon)',
                fontSize: 12,
                marginBottom: 12,
              }}
            >
              {t('films.scanModal.result', {
                found: result.found,
                registered: result.registered,
                skipped: result.skipped,
              })}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={props.onClose}>
              {t('common.close')}
            </button>
            <button type="submit" className="primary" disabled={scanMutation.isPending}>
              {scanMutation.isPending
                ? t('films.scanModal.submitting')
                : t('films.scanModal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ScrapeDialog(props: { onClose: () => void; onSuccess: () => void }): React.ReactElement {
  const { t } = useTranslation();
  const [platform, setPlatform] = useState<'tiktok' | 'instagram' | 'youtube' | 'twitter' | 'vk'>('tiktok');
  const [queries, setQueries] = useState('');
  const [topN, setTopN] = useState(10);
  const [minPlayCount, setMinPlayCount] = useState(1000);
  const [maxDurationSec, setMaxDurationSec] = useState(60);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof scrapeFilms>> | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      setError(null);
      const list = queries
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (list.length === 0) throw new Error('Provide at least one keyword');
      return scrapeFilms({ platform, queries: list, topN, minPlayCount, maxDurationSec });
    },
    onSuccess: (r) => setResult(r),
    onError: (err) => setError((err as Error).message),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    mutation.mutate();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={props.onClose}
    >
      <div
        style={{
          width: 520,
          maxWidth: '95vw',
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          padding: 24,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, marginBottom: 16 }}>{t('films.scrapeModal.title')}</h2>

        {result ? (
          <div>
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              {t('films.scrapeModal.successSummary', {
                platform: result.platform,
                enqueued: result.enqueuedCount,
                raw: result.rawCount,
                filtered: result.filtered,
              })}
            </div>
            {result.films.length > 0 && (
              <ul style={{ fontSize: 12, color: 'var(--text-3)', listStyle: 'none', padding: 0, maxHeight: 200, overflowY: 'auto' }}>
                {result.films.map((f) => (
                  <li key={f.filmId} style={{ padding: '4px 0' }}>
                    {f.authorHandle ?? '—'} · {f.viewCount?.toLocaleString() ?? '?'} views ·{' '}
                    <a href={f.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>
                      {f.sourceUrl.slice(0, 60)}…
                    </a>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="primary" onClick={props.onSuccess}>
                {t('common.close')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="form-row">
              <label>{t('films.scrapeModal.platformLabel')}</label>
              <select value={platform} onChange={(e) => setPlatform(e.target.value as 'tiktok' | 'instagram' | 'youtube' | 'twitter' | 'vk')}>
                <option value="tiktok">TikTok</option>
                <option value="instagram">Instagram</option>
                <option value="youtube">YouTube Shorts</option>
                <option value="twitter">X / Twitter</option>
                <option value="vk">VK</option>
              </select>
              {platform === 'instagram' && (
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                  {t('films.scrapeModal.instagramHint')}
                </div>
              )}
              {platform === 'youtube' && (
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                  {t('films.scrapeModal.youtubeHint')}
                </div>
              )}
              {platform === 'twitter' && (
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                  {t('films.scrapeModal.twitterHint')}
                </div>
              )}
              {platform === 'vk' && (
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                  {t('films.scrapeModal.vkHint')}
                </div>
              )}
            </div>
            <div className="form-row">
              <label>{t('films.scrapeModal.queriesLabel')}</label>
              <textarea
                value={queries}
                onChange={(e) => setQueries(e.target.value)}
                placeholder={t('films.scrapeModal.queriesPlaceholder')}
                rows={3}
                required
              />
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                {t('films.scrapeModal.queriesHint')}
              </div>
            </div>
            <div className="form-row inline">
              <div>
                <label>{t('films.scrapeModal.topNLabel')}</label>
                <input type="number" min={1} max={50} value={topN} onChange={(e) => setTopN(Number(e.target.value))} />
              </div>
              <div>
                <label>{t('films.scrapeModal.minPlayCountLabel')}</label>
                <input type="number" min={0} step={100} value={minPlayCount} onChange={(e) => setMinPlayCount(Number(e.target.value))} />
              </div>
              <div>
                <label>{t('films.scrapeModal.maxDurationLabel')}</label>
                <input type="number" min={5} max={600} value={maxDurationSec} onChange={(e) => setMaxDurationSec(Number(e.target.value))} />
              </div>
            </div>
            {error && (
              <div style={{ color: 'var(--hot)', fontSize: 12, marginBottom: 12 }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" onClick={props.onClose}>{t('common.cancel')}</button>
              <button type="submit" className="primary" disabled={mutation.isPending}>
                {mutation.isPending ? t('films.scrapeModal.scraping') : t('films.scrapeModal.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
